import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
    ArrowLeft,
    Play,
    Pause,
    Download,
    FileText,
    Loader2,
    Volume2,
    Clock,
    User,
    Users,
    RefreshCw,
    RotateCw,
    AlertCircle,
    Database,
    Mic,
} from "lucide-react";
import { useMeeting } from "../../../../context/MeetingContext";
import {
    BrainBankExport,
    ImportedTranscriptMeta,
    MeetingSession,
    Transcription,
    RecordingStatus,
} from "../../../../types";
import {
    deleteMeetingAudio,
    deleteMeetingImportedTranscript,
    getMeetingImportedPdfDownloadUrl,
    getMeetingTranscriptDocument,
    listMeetingBrainBankExports,
} from "../../../../service/apiService";
import SendToBrainBankModal from "./SendToBrainBankModal";
import BrainBankExportsCard from "./BrainBankExportsCard";
import AskVirtualAssistantButton from "./AskVirtualAssistantButton";
import ImportedTranscriptPanel from "./ImportedTranscriptPanel";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

interface MeetingDetailViewProps {
    session: MeetingSession;
    onBack: () => void;
    onEditSpeakers?: () => void;
    /** Closes the Meetings dialog -- used when leaving for the chat. */
    onClose?: () => void;
}

// Color palette for speakers (matching TranscriptionViewer)
const SPEAKER_COLORS = [
    { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300' },
    { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
    { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300' },
];

// Get color for speaker
const getSpeakerColor = (speakerLabel: string) => {
    const match = speakerLabel.match(/spk_(\d+)/);
    const index = match ? parseInt(match[1]) : 0;
    return SPEAKER_COLORS[index % SPEAKER_COLORS.length];
};

const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

interface AudioPlayerCardProps {
    audioUrl: string;
    canDelete: boolean;
    onDownload: () => void;
    onRequestDelete: () => void;
    // Presigned URLs expire; the parent fetches a fresh one and swaps
    // the audioUrl prop.
    onUrlExpired: () => void;
}

// Owns all playback state so the ~4x/second `timeupdate` re-renders
// stay inside this card instead of re-rendering the whole detail view
// (including the full transcript list).
const AudioPlayerCard: React.FC<AudioPlayerCardProps> = React.memo(({
    audioUrl,
    canDelete,
    onDownload,
    onRequestDelete,
    onUrlExpired,
}) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const refreshedRef = useRef(false);

    // A new URL gets a fresh one-shot expiry refresh.
    useEffect(() => {
        refreshedRef.current = false;
    }, [audioUrl]);

    const togglePlay = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const time = parseFloat(e.target.value);
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };

    const handleError = () => {
        // Most likely an expired presigned URL — silently refresh once.
        if (refreshedRef.current) return;
        refreshedRef.current = true;
        setIsPlaying(false);
        onUrlExpired();
    };

    return (
        <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
                <Volume2 className="w-5 h-5 text-gray-600" />
                <h3 className="font-medium text-gray-900">Audio Recording</h3>
            </div>

            <audio
                ref={audioRef}
                src={audioUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setIsPlaying(false)}
                onError={handleError}
                className="hidden"
            />

            {/* On phones the scrubber wraps onto its own full-width row
                (order-3) so it is actually draggable; from sm it sits back
                between the play button and the icon buttons as before. */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                <button
                    onClick={togglePlay}
                    className="order-1 flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700 transition-colors"
                >
                    {isPlaying ? (
                        <Pause className="w-5 h-5" />
                    ) : (
                        <Play className="w-5 h-5 ml-0.5" />
                    )}
                </button>

                <div className="order-3 w-full min-w-0 sm:order-2 sm:w-auto sm:flex-1">
                    <input
                        type="range"
                        min={0}
                        max={duration || 0}
                        value={currentTime}
                        onChange={handleSeek}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                </div>

                <div className="order-2 ml-auto flex items-center gap-1 sm:order-3 sm:ml-0 sm:gap-4">
                    <button
                        onClick={onDownload}
                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                        title="Download audio"
                    >
                        <Download className="w-5 h-5 text-gray-600" />
                    </button>

                    {canDelete && (
                        <button
                            onClick={onRequestDelete}
                            className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                            title="Delete audio (transcript is kept)"
                        >
                            <Trash2 className="w-5 h-5 text-gray-400 hover:text-red-500" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
});
AudioPlayerCard.displayName = 'AudioPlayerCard';

const MeetingDetailView: React.FC<MeetingDetailViewProps> = ({ session, onBack, onEditSpeakers, onClose }) => {
    const { getDownloadUrl, getTranscription, pollTranscriptionStatus, refreshSession, retryTranscription } = useMeeting();

    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [showDeleteAudioConfirm, setShowDeleteAudioConfirm] = useState(false);
    const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
    const [deletingAudio, setDeletingAudio] = useState(false);
    const [audioDeletedAt, setAudioDeletedAt] = useState<string | null>(session.audio_deleted_at || null);
    const [transcription, setTranscription] = useState<Transcription | null>(null);
    const [loading, setLoading] = useState(true);
    const [transcriptionLoading, setTranscriptionLoading] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    // WS-02 — BrainBank copies of this transcript
    const [showSendToBrainBank, setShowSendToBrainBank] = useState(false);
    const [brainBankExports, setBrainBankExports] = useState<BrainBankExport[]>(
        session.brain_bank_exports || []
    );
    // WS-03 — the Imported transcript tab. Every meeting shows both tabs; the
    // imported one holds either the PDF's text or the import drop zone.
    const [activeTab, setActiveTab] = useState<'live' | 'imported'>('live');
    const [importedMeta, setImportedMeta] = useState<ImportedTranscriptMeta | null>(
        session.imported_transcript || null
    );
    const [showDeleteImportedConfirm, setShowDeleteImportedConfirm] = useState(false);
    const [deletingImported, setDeletingImported] = useState(false);

    // Check if session has been stuck in processing/transcribing for more than 3 hours
    const isStuckProcessing = (): boolean => {
        if (session.recording_status === 'failed') return true;
        if (session.recording_status !== 'processing' && session.recording_status !== 'transcribing') return false;
        const statusTime = new Date(session.status_changed_at || session.updated_at).getTime();
        const threeHoursMs = 3 * 60 * 60 * 1000;
        return (Date.now() - statusTime) > threeHoursMs;
    };

    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const loadedSessionIdRef = useRef<string | null>(null);

    // Clean up polling on unmount
    useEffect(() => {
        return () => {
            if (pollingRef.current) {
                clearTimeout(pollingRef.current);
            }
        };
    }, []);

    // Load audio URL and transcription on mount or when status changes.
    // Skips anything already loaded — the status transition after a
    // completed poll (transcribing -> transcribed via refreshSession)
    // re-runs this effect, and the poll already fetched the data.
    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            const status = session.recording_status;
            const isNewSession = loadedSessionIdRef.current !== session.id;
            loadedSessionIdRef.current = session.id;
            if (isNewSession) {
                setAudioUrl(null);
                setTranscription(null);
                setAudioDeletedAt(session.audio_deleted_at || null);
                setActiveTab('live');
                setImportedMeta(session.imported_transcript || null);
            }

            const needsAudioUrl = isAudioReady(status)
                && !session.audio_deleted_at
                && (isNewSession || !audioUrl);
            const needsTranscription = (status === 'transcribed' || status === 'completed')
                && (isNewSession || !transcription);

            try {
                // Get audio download URL if recording is done
                if (needsAudioUrl) {
                    const url = await getDownloadUrl(session.id);
                    if (!cancelled && url) {
                        setAudioUrl(url);
                    }
                }

                if (needsTranscription) {
                    setTranscriptionLoading(true);
                    try {
                        const trans = await getTranscription(session.id);
                        if (!cancelled) {
                            setTranscription(trans);
                        }
                    } finally {
                        if (!cancelled) {
                            setTranscriptionLoading(false);
                        }
                    }
                }

                if (status === 'transcribing' || status === 'processing') {
                    startPolling();
                }
            } catch (error) {
                console.error('Error loading meeting data:', error);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.id, session.recording_status]);

    // The session prop carries the list, but a send or a removal happens after
    // it was loaded — re-read from the source rather than trusting the prop.
    const refreshBrainBankExports = useCallback(async () => {
        try {
            setBrainBankExports(await listMeetingBrainBankExports(session.id));
        } catch (error) {
            console.error('Error loading BrainBank copies:', error);
        }
    }, [session.id]);

    useEffect(() => {
        refreshBrainBankExports();
    }, [refreshBrainBankExports]);

    const isAudioReady = (status: RecordingStatus): boolean => {
        if (status === 'failed') {
            // Transcription failing leaves the recording perfectly intact, and
            // that is precisely when someone wants to play it back, download it
            // or delete it (the delete button lives inside the player). A
            // failure during assembly has no audio_s3_key, so it stays hidden
            // rather than asking the server for a recording that never existed.
            return !!session.audio_s3_key;
        }
        return ['completed', 'transcribed', 'transcribing', 'processing'].includes(status);
    };

    const loadTranscription = async () => {
        setTranscriptionLoading(true);
        try {
            const trans = await getTranscription(session.id);
            setTranscription(trans);
        } catch (error) {
            console.error('Error loading transcription:', error);
        } finally {
            setTranscriptionLoading(false);
        }
    };

    const isPollingCheckRef = useRef(false);

    // Polling for transcription status
    const startPolling = () => {
        if (pollingRef.current) {
            clearTimeout(pollingRef.current);
        }
        setIsPolling(true);
        pollForCompletion();
    };

    const pollForCompletion = async () => {
        if (isPollingCheckRef.current) return;

        // Don't hit the API while the tab is hidden; check again later.
        if (document.hidden) {
            pollingRef.current = setTimeout(pollForCompletion, 30000);
            return;
        }

        isPollingCheckRef.current = true;

        try {
            const status = await pollTranscriptionStatus(session.id);

            if (status.status === 'COMPLETED') {
                setIsPolling(false);
                await loadTranscription();
                await refreshSession(session.id);
                toast.success('Transcription is ready!');
            } else if (status.status === 'FAILED') {
                setIsPolling(false);
                await refreshSession(session.id);
                toast.error('Transcription failed');
            } else {
                // Continue polling
                pollingRef.current = setTimeout(pollForCompletion, 30000);
            }
        } catch (error) {
            console.error('Polling error:', error);
            setIsPolling(false);
        } finally {
            isPollingCheckRef.current = false;
        }
    };

    const handleRefreshStatus = async () => {
        try {
            const status = await pollTranscriptionStatus(session.id);

            if (status.status === 'COMPLETED') {
                toast.success('Transcription is ready!');
                await loadTranscription();
                await refreshSession(session.id);
            } else if (status.status === 'IN_PROGRESS') {
                toast.info('Transcription is still processing...');
                startPolling();
            } else if (status.status === 'FAILED') {
                toast.error('Transcription failed');
                await refreshSession(session.id);
            }
        } catch (error) {
            console.error('Error refreshing status:', error);
            toast.error('Failed to refresh status');
        }
    };

    // Get speaker name from mapping
    const getSpeakerName = useCallback((speakerLabel: string): string => {
        const mapping = session.speaker_mapping?.find(m => m.speaker_label === speakerLabel);
        if (mapping?.participant_name) {
            return mapping.participant_name;
        }
        const match = speakerLabel.match(/spk_(\d+)/);
        return match ? `Speaker ${parseInt(match[1]) + 1}` : speakerLabel;
    }, [session.speaker_mapping]);

    const formatDuration = (seconds: number | undefined) => {
        if (!seconds) return '--:--';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Delete only the audio, keeping the transcript (WS-01 / D17).
    // Clearing audioUrl unmounts the player, which stops playback.
    const handleDeleteAudio = async () => {
        setDeletingAudio(true);
        try {
            await deleteMeetingAudio(session.id);
            setAudioUrl(null);
            setAudioDeletedAt(new Date().toISOString());
            setShowDeleteAudioConfirm(false);
            toast.success('Audio deleted. The transcript has been kept.');
            await refreshSession(session.id);
        } catch (err) {
            console.error('Error deleting audio:', err);
            toast.error('Failed to delete audio');
        } finally {
            setDeletingAudio(false);
        }
    };

    // The player reports a media error (typically an expired presigned
    // URL); swap in a fresh one.
    const handleAudioUrlExpired = useCallback(async () => {
        const url = await getDownloadUrl(session.id);
        if (url) {
            setAudioUrl(url);
        }
    }, [getDownloadUrl, session.id]);

    const handleRequestDeleteAudio = useCallback(() => {
        setShowDeleteAudioConfirm(true);
    }, []);

    // Regenerate the transcription from the audio file (completed
    // sessions). Replaces the transcript and clears speaker names, so
    // it sits behind a confirmation. Once the session refreshes into
    // 'transcribing', the existing load effect takes over the polling.
    const handleRegenerate = async () => {
        setShowRegenerateConfirm(false);
        setIsRetrying(true);
        try {
            setTranscription(null);
            await retryTranscription(session.id, true);
        } finally {
            setIsRetrying(false);
        }
    };

    // WS-03 — after an import the meta comes straight from the response; the
    // session refresh follows so the prop catches up, and the BrainBank list
    // re-reads because the server re-synced every copy automatically.
    const handleImported = useCallback(async (meta: ImportedTranscriptMeta) => {
        setImportedMeta(meta);
        await refreshBrainBankExports();
        refreshSession(session.id);
    }, [refreshBrainBankExports, refreshSession, session.id]);

    // The original PDF is provenance — downloadable, never indexed. The
    // presigned URL carries an attachment disposition, so a plain navigation
    // saves it under its uploaded name.
    const handleDownloadImportedPdf = useCallback(async () => {
        try {
            const url = await getMeetingImportedPdfDownloadUrl(session.id);
            if (!url) {
                toast.error('File not available');
                return;
            }
            const a = document.createElement('a');
            a.href = url;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (err) {
            console.error('Error downloading imported PDF:', err);
            toast.error('Failed to download the PDF');
        }
    }, [session.id]);

    // One confirmation, then gone everywhere: the server also updates every
    // BrainBank copy, so nothing here needs a second decision.
    const handleDeleteImported = async () => {
        setDeletingImported(true);
        try {
            const result = await deleteMeetingImportedTranscript(session.id);
            setShowDeleteImportedConfirm(false);
            setImportedMeta(null);
            toast.success(result.message || 'Imported transcript deleted.');
            if (result.brain_bank_sync?.failed) {
                toast.error(
                    `${result.brain_bank_sync.failed} BrainBank cop${result.brain_bank_sync.failed === 1 ? 'y' : 'ies'} could not be updated.`
                );
            }
            await refreshBrainBankExports();
            refreshSession(session.id);
        } catch (err) {
            console.error('Error deleting imported transcript:', err);
            toast.error('Failed to delete the imported transcript');
        } finally {
            setDeletingImported(false);
        }
    };

    // An empty transcript object is not a transcript: the actions that act on
    // its content stay hidden rather than producing a header-only download or a
    // server-side refusal. Same rule the export service applies.
    const hasTranscriptContent = !!(
        transcription?.content?.some((segment) => (segment.content || '').trim())
        || (transcription?.full_text || '').trim()
    );

    const canRegenerate = session.recording_status === 'completed' && !!audioUrl && !audioDeletedAt;

    // Download handlers
    const handleDownloadAudio = useCallback(async () => {
        try {
            // The URL loaded at mount may have expired — get a fresh one.
            const url = (await getDownloadUrl(session.id)) || audioUrl;
            if (!url) {
                toast.error('Audio not available');
                return;
            }

            // S3 answers a rejected presigned URL with a 200-less response
            // carrying an XML error body. fetch() resolves happily on that, so
            // without this check the error document is saved as a .wav and the
            // user is told the download succeeded — the file just plays silence.
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`S3 refused the download URL: ${response.status}`);
            }
            const blob = await response.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = `${session.title || 'recording'}.wav`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(objectUrl);
            document.body.removeChild(a);
            toast.success('Audio downloaded');
        } catch (error) {
            console.error('Error downloading audio:', error);
            toast.error('Failed to download audio');
        }
    }, [getDownloadUrl, session.id, session.title, audioUrl]);

    // The server renders the document, so what is downloaded here and what a
    // BrainBank indexes are the same bytes. Two renderers for one clinical
    // document would drift within a release.
    const handleDownloadTranscript = async () => {
        try {
            const blob = await getMeetingTranscriptDocument(session.id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${session.title || 'transcript'}.txt`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Transcript downloaded');
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === 'string' ? detail : 'Transcription not available'
            );
        }
    };

    const getStatusColor = (status: RecordingStatus) => {
        switch (status) {
            case 'transcribed': return 'bg-green-100 text-green-800';
            case 'completed': return 'bg-green-100 text-green-800';
            case 'transcribing': return 'bg-yellow-100 text-yellow-800';
            case 'processing': return 'bg-yellow-100 text-yellow-800';
            case 'failed': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Memoized so audio playback and polling state changes don't re-run
    // the per-segment speaker lookups or re-render the whole list.
    const transcriptSegments = useMemo(() => {
        if (!transcription?.content?.length) return null;
        return transcription.content.map((segment, index) => {
            const color = getSpeakerColor(segment.speaker_label);
            return (
                // Phones stack the speaker chip above what they said, so the
                // spoken text gets the full width instead of a ~120px column.
                <div key={index} className="flex flex-col sm:flex-row gap-1 sm:gap-3">
                    <div className="flex-shrink-0">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 ${color.bg} ${color.text} rounded text-xs font-medium`}>
                            <User className="w-3 h-3" />
                            {getSpeakerName(segment.speaker_label)}
                        </span>
                    </div>
                    <p className="text-gray-700 flex-1 min-w-0 break-words">{segment.content}</p>
                </div>
            );
        });
    }, [transcription, getSpeakerName]);

    const getStatusLabel = (status: RecordingStatus) => {
        switch (status) {
            case 'transcribed': return 'Transcribed';
            case 'completed': return 'Completed';
            case 'transcribing': return 'Transcribing...';
            case 'processing': return 'Processing...';
            case 'failed': return 'Failed';
            case 'recording': return 'Recording';
            case 'uploading': return 'Uploading';
            default: return status;
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <button
                        onClick={onBack}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div className="min-w-0">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                            {session.title || 'Untitled Recording'}
                        </h2>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatDuration(session.audio_duration_seconds)}
                            </span>
                            <span>
                                {new Date(session.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 pl-11 sm:pl-0">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${getStatusColor(session.recording_status)}`}>
                        {getStatusLabel(session.recording_status)}
                    </span>
                    {(session.recording_status === 'transcribing' || session.recording_status === 'processing') && !isPolling && (
                        <button
                            onClick={handleRefreshStatus}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            title="Refresh status"
                        >
                            <RefreshCw className="w-4 h-4 text-gray-600" />
                        </button>
                    )}
                    {isStuckProcessing() && (
                        <button
                            onClick={async () => {
                                setIsRetrying(true);
                                try {
                                    await retryTranscription(session.id);
                                } finally {
                                    setIsRetrying(false);
                                }
                            }}
                            className="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Retry Transcription"
                            disabled={isRetrying}
                        >
                            {isRetrying ? 'Retrying...' : 'Retry'}
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto space-y-6">
                    {/* Audio Player */}
                    {audioUrl && (
                        <AudioPlayerCard
                            audioUrl={audioUrl}
                            canDelete={session.recording_status === 'completed' || session.recording_status === 'failed'}
                            onDownload={handleDownloadAudio}
                            onRequestDelete={handleRequestDeleteAudio}
                            onUrlExpired={handleAudioUrlExpired}
                        />
                    )}

                    {/* Audio deleted note (WS-01 / D17) */}
                    {!audioUrl && audioDeletedAt && (
                        <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3 text-sm text-gray-500">
                            <Volume2 className="w-5 h-5 text-gray-400" />
                            Audio was deleted on {new Date(audioDeletedAt).toLocaleDateString()}. The transcript has been kept.
                        </div>
                    )}

                    {/* Transcription — live and imported (WS-03) side by side */}
                    <div className="bg-white border border-gray-200 rounded-xl">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 p-3 sm:p-4">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-gray-600 flex-shrink-0" />
                                <h3 className="font-medium text-gray-900">Transcription</h3>
                            </div>
                            {/* Up to five controls live here — they wrap on
                                phones and drop their labels below sm. */}
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 sm:justify-end">
                                {activeTab === 'live' && canRegenerate && (
                                    <button
                                        onClick={() => setShowRegenerateConfirm(true)}
                                        disabled={isRetrying}
                                        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50"
                                        title="Re-transcribe from the audio recording"
                                    >
                                        <RotateCw className={`w-4 h-4 flex-shrink-0 ${isRetrying ? 'animate-spin' : ''}`} />
                                        <span className="hidden sm:inline">{isRetrying ? 'Starting…' : 'Re-transcribe'}</span>
                                    </button>
                                )}
                                {activeTab === 'live' && onEditSpeakers && transcription && (
                                    <button
                                        onClick={onEditSpeakers}
                                        title="Edit speaker names"
                                        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                                    >
                                        <Users className="w-4 h-4 flex-shrink-0" />
                                        <span className="hidden sm:inline">Edit Speakers</span>
                                        <span className="sm:hidden">Speakers</span>
                                    </button>
                                )}
                                {activeTab === 'imported' && importedMeta && (
                                    <button
                                        onClick={() => setShowDeleteImportedConfirm(true)}
                                        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                                        title="Delete the imported transcript from this meeting and every BrainBank"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </button>
                                )}
                                {activeTab === 'live' && hasTranscriptContent && (
                                    <button
                                        onClick={handleDownloadTranscript}
                                        className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-700"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download
                                    </button>
                                )}
                                {activeTab === 'imported' && importedMeta && (
                                    <button
                                        onClick={handleDownloadImportedPdf}
                                        className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-700"
                                        title="Download the original PDF"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download
                                    </button>
                                )}
                                {(hasTranscriptContent || importedMeta) && (
                                    <button
                                        onClick={() => setShowSendToBrainBank(true)}
                                        className="flex items-center gap-1 text-sm font-medium text-violet-700 border border-violet-600 bg-violet-50 hover:bg-violet-100 px-2.5 py-1 rounded-lg transition-colors"
                                        title="Send this meeting's transcripts to a BrainBank"
                                    >
                                        <Database className="w-4 h-4 flex-shrink-0" />
                                        <span className="hidden sm:inline">Send to BrainBank</span>
                                        <span className="sm:hidden">BrainBank</span>
                                    </button>
                                )}
                                {/* Pick any VA and ask it about this meeting: the
                                    BrainBank rides along with the chat, so no
                                    attachment has to be set up first. */}
                                <AskVirtualAssistantButton
                                    exports={brainBankExports}
                                    onClose={onClose}
                                />
                            </div>
                        </div>

                        {/* WS-03 — both tabs are always visible: the imported one
                            holds either the PDF's text or the import drop zone. */}
                        <div className="flex items-center gap-4 sm:gap-6 px-3 sm:px-4 border-b border-gray-200 overflow-x-auto">
                            <button
                                onClick={() => setActiveTab('live')}
                                className={`flex items-center gap-1.5 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                                    activeTab === 'live'
                                        ? 'text-violet-700 font-semibold border-violet-600'
                                        : 'text-gray-500 font-medium border-transparent hover:text-gray-700'
                                }`}
                            >
                                <Mic className="w-4 h-4 flex-shrink-0" />
                                <span className="whitespace-nowrap">Live transcript</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('imported')}
                                className={`flex items-center gap-1.5 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                                    activeTab === 'imported'
                                        ? 'text-violet-700 font-semibold border-violet-600'
                                        : 'text-gray-500 font-medium border-transparent hover:text-gray-700'
                                }`}
                            >
                                <FileText className="w-4 h-4 flex-shrink-0" />
                                <span className="whitespace-nowrap">Imported transcript</span>
                            </button>
                            {/* Filename/page meta is secondary — it would push
                                the tabs off-screen on a phone, so it starts at sm. */}
                            {activeTab === 'imported' && importedMeta && (
                                <span className="hidden sm:inline ml-auto text-xs text-gray-400 truncate">
                                    <span className="font-mono">{importedMeta.file_name}</span>
                                    {importedMeta.page_count ? ` · ${importedMeta.page_count} page${importedMeta.page_count === 1 ? '' : 's'}` : ''}
                                    {importedMeta.imported_at ? ` · imported ${new Date(importedMeta.imported_at).toLocaleDateString()}` : ''}
                                </span>
                            )}
                        </div>

                        {activeTab === 'imported' ? (
                            <div className="p-3 sm:p-4 max-h-96 overflow-y-auto">
                                <ImportedTranscriptPanel
                                    sessionId={session.id}
                                    importedMeta={importedMeta}
                                    onImported={handleImported}
                                />
                            </div>
                        ) : (
                        <div className="p-4 max-h-96 overflow-y-auto">
                            {transcriptionLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                                </div>
                            ) : isPolling || session.recording_status === 'transcribing' ? (
                                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                    <Loader2 className="w-8 h-8 text-violet-500 animate-spin mb-3" />
                                    <p>Transcription in progress...</p>
                                    <p className="text-sm mt-1">This may take a few minutes</p>
                                </div>
                            ) : session.recording_status === 'failed' ? (
                                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                    <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
                                    <p>Transcription failed</p>
                                    {/* AWS's own reason when we have it. "Failed" alone
                                        sends people to the logs for something the
                                        service already told us. */}
                                    {session.transcription_failure_reason && (
                                        <p className="text-sm mt-1 max-w-md text-center">
                                            {session.transcription_failure_reason}
                                        </p>
                                    )}
                                    <button
                                        onClick={async () => {
                                            setIsRetrying(true);
                                            try {
                                                await retryTranscription(session.id);
                                            } finally {
                                                setIsRetrying(false);
                                            }
                                        }}
                                        className="mt-3 flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors disabled:opacity-50"
                                        disabled={isRetrying}
                                    >
                                        {isRetrying ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <RotateCw className="w-4 h-4" />
                                        )}
                                        {isRetrying ? 'Retrying...' : 'Retry Transcription'}
                                    </button>
                                </div>
                            ) : transcriptSegments ? (
                                <div className="space-y-4">
                                    {transcriptSegments}
                                </div>
                            ) : transcription?.full_text ? (
                                <p className="text-gray-700 whitespace-pre-wrap">{transcription.full_text}</p>
                            ) : (
                                <p className="text-gray-500 text-center py-4">No transcription available</p>
                            )}
                        </div>
                        )}
                    </div>

                    {/* BrainBank copies (WS-02) */}
                    <BrainBankExportsCard
                        sessionId={session.id}
                        exports={brainBankExports}
                        importedFileName={importedMeta?.file_name || null}
                        onChanged={refreshBrainBankExports}
                    />

                    {/* Meeting Info */}
                    {session.description && (
                        <div className="bg-gray-50 rounded-xl p-4">
                            <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                            <p className="text-gray-600">{session.description}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Send to BrainBank (WS-02) */}
            {showSendToBrainBank && (
                <SendToBrainBankModal
                    session={session}
                    onClose={() => setShowSendToBrainBank(false)}
                    onExported={refreshBrainBankExports}
                    onEditSpeakers={
                        onEditSpeakers
                            ? () => {
                                  setShowSendToBrainBank(false);
                                  onEditSpeakers();
                              }
                            : undefined
                    }
                />
            )}

            {/* Re-transcribe confirmation */}
            {showRegenerateConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-violet-50 rounded-full flex items-center justify-center mb-4">
                                <RotateCw className="w-8 h-8 text-violet-500" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Re-transcribe this recording?
                            </h3>
                            <p className="text-sm text-gray-600 mb-6">
                                A new transcription will be generated from the audio
                                file. The current transcript and speaker names will
                                be replaced. This may take a few minutes.
                            </p>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setShowRegenerateConfirm(false)}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleRegenerate}
                                    className="px-5 py-2.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
                                >
                                    Re-transcribe
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete imported transcript confirmation (WS-03) — one decision:
                it disappears from the meeting and from every BrainBank copy. */}
            {showDeleteImportedConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                                <Trash2 className="w-8 h-8 text-red-500" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Delete imported transcript?
                            </h3>
                            <p className="text-sm text-gray-600 mb-6">
                                This deletes{' '}
                                {importedMeta?.file_name ? (
                                    <span className="font-mono">{importedMeta.file_name}</span>
                                ) : (
                                    'the imported transcript'
                                )}{' '}
                                and its transcript from this meeting and from every
                                BrainBank it was sent to. The live recording and its
                                transcript are not affected.
                            </p>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setShowDeleteImportedConfirm(false)}
                                    disabled={deletingImported}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteImported}
                                    disabled={deletingImported}
                                    className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                >
                                    {deletingImported ? 'Deleting…' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete audio confirmation (WS-01 / D17) */}
            {showDeleteAudioConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center scrim">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                                <Trash2 className="w-8 h-8 text-red-500" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Delete audio recording?
                            </h3>
                            <p className="text-sm text-gray-600 mb-6">
                                The transcript will be kept. The audio cannot be
                                recovered, and re-transcribing this session will no
                                longer be possible.
                            </p>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setShowDeleteAudioConfirm(false)}
                                    disabled={deletingAudio}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteAudio}
                                    disabled={deletingAudio}
                                    className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                >
                                    {deletingAudio ? 'Deleting…' : 'Delete audio'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MeetingDetailView;
